import { Injectable } from '@angular/core';
import {HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest, HttpResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import { AppInsightsLoggerService } from './app-insights-logger.service';
import {GlobalConstants} from '../global-constants';

@Injectable({
  providedIn: 'root'
})
/**
 * This class intercepts all HTTP calls and then based on whether the call was successful
 * or an error occurred during the call, it invokes Logging service log method through which
 * it sends the acquired information to the backend logging API.
 */
export class GlobalHttpLoggingInterceptorService implements HttpInterceptor {
  constructor(public router: Router, private appInsightsLoggerService: AppInsightsLoggerService) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap(event => {
        /*
         * We want to call the Logger service log method only when the frontend is not making
         * calls to the "/ui-logger" endpoint. The reason being that the "/ui-logger" endpoint is the
         * Backend API path to which the Logger service log method makes a call to post Log information.
         * If we call the Logger service log method when there is an error
         * posting to "/ui-logger" path then an infinite loop will occur.
         * When running in the local 'demo' config, the endpoint is "/assets/demo/mhs/log-response.json" instead of "/ui-logger"
         */
        if (event instanceof HttpResponse && !req.url.includes('/ui-logger') && !req.url.includes('/assets/demo/mhs/log-response.json')){
          // Checking whether our HTTP call was successful inorder to display appropriate Log message.
          if (event.status === 200 || event.status === 201) {
            this.appInsightsLoggerService.log('INFO', 'Info log from GlobalHttpInterceptorService', `Made a successful ${req.method} request to URL = ${this.removeSpecifiedParameterFromUrl(req.url, GlobalConstants.API_SUBSCRIPTION_PARAM_NAME)}. Response status code received = ${event.status}. Response status text received = ${event.statusText}`);
          }
        }})
      , catchError((error) => {
        if (!req.url.includes('/ui-logger')) {
          this.appInsightsLoggerService.log('ERROR', 'Error log from GlobalHttpInterceptorService', `Failed to make a ${req.method} request to URL = ${this.removeSpecifiedParameterFromUrl(req.url, GlobalConstants.API_SUBSCRIPTION_PARAM_NAME)}. Error status code received = ${error.status}. Error status text received = ${error.statusText}`);
        }
        // After catching the error and logging it appropriately, we have to throw
        // the same error back so that it could be properly handled by the initial caller who initiated the HTTP call.
        return throwError(error);
      })
    );
  }

  /**
   *  Method that removes a given request parameter from the specified URL
   * @param url A string URL from which the provided request parameter needs to be removed
   * @param parameterToRemove Request parameter that needs to be removed.
   * @private
   */
  private removeSpecifiedParameterFromUrl(url: string, parameterToRemove: string): string {
    const urlComponents = url.split('?');
    let finalUrl = urlComponents[0];
    if (urlComponents.length >= 2) {
      const parameters = urlComponents[1].split(/[&;]/g);
      let addedParamCount = 0;
      const paramsLength = parameters.length;
      for (let index = 0; index < paramsLength; index++) {
        const currentParam = parameters[index];
        if (String(currentParam).toUpperCase().includes(parameterToRemove.toUpperCase())) {
          continue;
        }
        if (addedParamCount > 0) {
          finalUrl = finalUrl.concat('&' + currentParam);
        } else {
          finalUrl = finalUrl.concat('?' + currentParam);
        }
        addedParamCount++;
      }
    }
    return finalUrl;
  }
}

